import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpenCheck,
  Calculator,
  Download,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { logModuloEvento } from '../../utils/bitacora';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

type EstadoBoleta = 'borrador' | 'pendiente' | 'revisada' | 'aprobada' | 'rechazada' | 'anulada';

interface BoletaRow {
  id: string;
  numero: string;
  fecha: string;
  semana: string | null;
  supervisor_nombre: string | null;
  finca_id: string | null;
  lote_id: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
  estadio_codigo: string | null;
  estado: EstadoBoleta;
  costo_total: number | null;
}

interface LaborRow {
  boleta_id: string;
  labor_id: string | null;
  labor_nombre: string | null;
  modalidad: string | null;
  unidad: string | null;
  cantidad: number | null;
  horas: number | null;
  tarifa: number | null;
  costo_labor: number | null;
}

interface BloqueRow {
  boleta_id: string;
  bloque_id: string | null;
  bloque_num: string | null;
  area_ha?: number | null;
  porcentaje_costo?: number | null;
}

interface TrabajadorRow {
  boleta_id: string;
  trabajador_codigo: string | null;
  trabajador_nombre: string | null;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  cantidad_produccion: number | null;
  estado_jornada?: string | null;
  aprobado_planilla?: boolean | null;
}

interface MaquinariaRow {
  boleta_id: string;
  equipo_id: string | null;
  equipo_codigo: string | null;
  equipo_nombre: string | null;
  operador_nombre: string | null;
  horas_equipo: number | null;
  costo_equipo: number | null;
}

interface CosteoLinea {
  id: string;
  boletaId: string;
  fecha: string;
  semana: string;
  boleta: string;
  estado: EstadoBoleta;
  fincaId: string | null;
  loteId: string | null;
  bloqueId: string | null;
  laborId: string | null;
  equipoId: string | null;
  equipoCodigo: string;
  equipoNombre: string;
  finca: string;
  lote: string;
  bloque: string;
  estadio: string;
  labor: string;
  modalidad: string;
  unidad: string;
  supervisor: string;
  horas: number;
  horasExtra: number;
  unidades: number;
  maquinaria: string;
  costoLabor: number;
  costoMaquinaria: number;
  costoTotal: number;
  cuenta: string;
  cuentaNombre: string;
  centroCosto: string;
  estadoContable: 'pendiente' | 'listo' | 'bloqueado';
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function weekLabel(fecha: string) {
  const d = new Date(`${fecha}T00:00:00`);
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${String(week).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { maximumFractionDigits: 0 });
}

function fmtNum(v?: number | null) {
  const n = Number(v ?? 0);
  return n ? n.toFixed(2) : '-';
}

function csvCell(value: unknown) {
  const s = String(value ?? '').replace(/"/g, '""');
  return `"${s}"`;
}

function cuentaSugerida(modalidad: string, tieneMaquinaria: boolean) {
  if (tieneMaquinaria) return { cuenta: '5-01-03-02', nombre: 'Costo maquinaria agricola' };
  if (modalidad === 'produccion' || modalidad === 'tarea') return { cuenta: '5-01-01-02', nombre: 'Mano de obra por tarea' };
  if (modalidad === 'fijo') return { cuenta: '5-01-01-03', nombre: 'Mano de obra fija campo' };
  return { cuenta: '5-01-01-01', nombre: 'Mano de obra por hora' };
}

function EstadoContableBadge({ estado }: { estado: CosteoLinea['estadoContable'] }) {
  const map = {
    listo: { label: 'Listo', color: 'var(--tone-success-text)' },
    pendiente: { label: 'Pendiente', color: 'var(--tone-info-text)' },
    bloqueado: { label: 'Bloqueado', color: '#f87171' },
  };
  const item = map[estado];
  return (
    <span className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: item.color, background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}>
      {item.label}
    </span>
  );
}

function Kpi({ label, value, icon, tone }: { label: string; value: string | number; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

export function CosteoContableLaboresPage() {
  const empresaId = useEmpresaId();
  const [fechaDesde, setFechaDesde] = useState(addDays(today(), -14));
  const [fechaHasta, setFechaHasta] = useState(today());
  const [estado, setEstado] = useState<'todos' | 'pendiente' | 'listo' | 'bloqueado'>('todos');
  const [q, setQ] = useState('');
  const [boletas, setBoletas] = useState<BoletaRow[]>([]);
  const [labores, setLabores] = useState<LaborRow[]>([]);
  const [bloques, setBloques] = useState<BloqueRow[]>([]);
  const [trabajadores, setTrabajadores] = useState<TrabajadorRow[]>([]);
  const [maquinaria, setMaquinaria] = useState<MaquinariaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [preparando, setPreparando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const permisos = useAppPermisos();
  const sort = useTableSort<'fecha' | 'boleta' | 'finca' | 'labor' | 'cuenta' | 'horas' | 'unidades' | 'costoLabor' | 'costoMaquinaria' | 'costoTotal' | 'estadoContable'>({ key: 'fecha', direction: 'desc' });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');

    const { data: b, error: eB } = await supabase
      .from('campo_boletas')
      .select('id, numero, fecha, semana, supervisor_nombre, finca_id, lote_id, finca_nombre, lote_num, estadio_codigo, estado, costo_total')
      .eq('empresa_id', empresaId)
      .gte('fecha', fechaDesde)
      .lte('fecha', fechaHasta)
      .order('fecha', { ascending: false });

    if (eB) {
      setError(eB.message);
      setBoletas([]);
      setLabores([]);
      setBloques([]);
      setTrabajadores([]);
      setMaquinaria([]);
      setLoading(false);
      return;
    }

    const ids = (b ?? []).map((row) => row.id);
    if (ids.length === 0) {
      setBoletas([]);
      setLabores([]);
      setBloques([]);
      setTrabajadores([]);
      setMaquinaria([]);
      setLoading(false);
      return;
    }

    const [lRes, blRes, tRes, mRes] = await Promise.all([
      supabase
        .from('campo_boleta_labores')
        .select('boleta_id, labor_id, labor_nombre, modalidad, unidad, cantidad, horas, tarifa, costo_labor')
        .eq('empresa_id', empresaId)
        .in('boleta_id', ids),
      supabase
        .from('campo_boleta_bloques')
        .select('boleta_id, bloque_id, bloque_num, area_ha, porcentaje_costo')
        .eq('empresa_id', empresaId)
        .in('boleta_id', ids),
      supabase
        .from('campo_boleta_trabajadores')
        .select('boleta_id, trabajador_codigo, trabajador_nombre, horas_ordinarias, horas_extra, cantidad_produccion, estado_jornada, aprobado_planilla')
        .eq('empresa_id', empresaId)
        .in('boleta_id', ids),
      supabase
        .from('campo_boleta_maquinaria')
        .select('boleta_id, equipo_id, equipo_codigo, equipo_nombre, operador_nombre, horas_equipo, costo_equipo')
        .eq('empresa_id', empresaId)
        .in('boleta_id', ids),
    ]);

    if (lRes.error || blRes.error || tRes.error || mRes.error) {
      setError(lRes.error?.message || blRes.error?.message || tRes.error?.message || mRes.error?.message || 'No se pudo cargar el costeo.');
    }

    setBoletas((b ?? []) as BoletaRow[]);
    setLabores((lRes.data ?? []) as LaborRow[]);
    setBloques((blRes.data ?? []) as BloqueRow[]);
    setTrabajadores((tRes.data ?? []) as TrabajadorRow[]);
    setMaquinaria((mRes.data ?? []) as MaquinariaRow[]);
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const lineas = useMemo<CosteoLinea[]>(() => {
    const laborMap = new Map<string, LaborRow[]>();
    const bloqueMap = new Map<string, BloqueRow[]>();
    const trabajadorMap = new Map<string, TrabajadorRow[]>();
    const maqMap = new Map<string, MaquinariaRow[]>();

    labores.forEach((l) => {
      if (!laborMap.has(l.boleta_id)) laborMap.set(l.boleta_id, []);
      laborMap.get(l.boleta_id)!.push(l);
    });
    bloques.forEach((b) => {
      if (!bloqueMap.has(b.boleta_id)) bloqueMap.set(b.boleta_id, []);
      bloqueMap.get(b.boleta_id)!.push(b);
    });
    trabajadores.forEach((t) => {
      if (!trabajadorMap.has(t.boleta_id)) trabajadorMap.set(t.boleta_id, []);
      trabajadorMap.get(t.boleta_id)!.push(t);
    });
    maquinaria.forEach((m) => {
      if (!maqMap.has(m.boleta_id)) maqMap.set(m.boleta_id, []);
      maqMap.get(m.boleta_id)!.push(m);
    });

    return boletas.flatMap((b) => {
      const labs = laborMap.get(b.id) || [];
      const bls = bloqueMap.get(b.id) || [];
      const trbs = trabajadorMap.get(b.id) || [];
      const maqs = maqMap.get(b.id) || [];
      const bloquesBase = bls.length > 0 ? bls : [{ boleta_id: b.id, bloque_id: null, bloque_num: '-' }];
      const costoBoleta = Number(b.costo_total ?? 0);
      const costoMaq = maqs.reduce((s, m) => s + Number(m.costo_equipo ?? 0), 0);
      const horas = trbs.reduce((s, t) => s + Number(t.horas_ordinarias ?? 0), 0);
      const horasExtra = trbs.reduce((s, t) => s + Number(t.horas_extra ?? 0), 0);
      const unidades = trbs.reduce((s, t) => s + Number(t.cantidad_produccion ?? 0), 0);
      const labor = labs[0];
      const modalidad = String(labor?.modalidad || 'hora');
      const cuenta = cuentaSugerida(modalidad, costoMaq > 0);
      const listaBloques = bloquesBase.map((bl) => bl.bloque_num || '-').join(', ');
      const divisor = Math.max(bloquesBase.length, 1);
      const estadoContable: CosteoLinea['estadoContable'] =
        b.estado === 'aprobada'
          ? (costoBoleta > 0 ? 'listo' : 'bloqueado')
          : b.estado === 'revisada'
            ? 'pendiente'
            : 'pendiente';

      return bloquesBase.map((bl, index) => ({
        id: `${b.id}-${index}`,
        boletaId: b.id,
        fecha: b.fecha,
        semana: b.semana || weekLabel(b.fecha),
        boleta: b.numero,
        estado: b.estado,
        fincaId: b.finca_id,
        loteId: b.lote_id,
        bloqueId: bl.bloque_id || null,
        laborId: labor?.labor_id || null,
        equipoId: maqs[0]?.equipo_id || null,
        equipoCodigo: maqs[0]?.equipo_codigo || '',
        equipoNombre: maqs[0]?.equipo_nombre || '',
        finca: b.finca_nombre || '-',
        lote: b.lote_num || '-',
        bloque: bl.bloque_num || listaBloques || '-',
        estadio: b.estadio_codigo || '-',
        labor: labor?.labor_nombre || '-',
        modalidad,
        unidad: labor?.unidad || '-',
        supervisor: b.supervisor_nombre || '-',
        horas: horas / divisor,
        horasExtra: horasExtra / divisor,
        unidades: unidades / divisor,
        maquinaria: maqs.map((m) => m.equipo_nombre).filter(Boolean).join(', ') || '-',
        costoLabor: Math.max(0, costoBoleta - costoMaq) / divisor,
        costoMaquinaria: costoMaq / divisor,
        costoTotal: costoBoleta / divisor,
        cuenta: cuenta.cuenta,
        cuentaNombre: cuenta.nombre,
        centroCosto: [b.finca_nombre, b.lote_num, bl.bloque_num].filter(Boolean).join(' / ') || '-',
        estadoContable,
      }));
    });
  }, [boletas, labores, bloques, trabajadores, maquinaria]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = lineas.filter((l) => {
      if (estado !== 'todos' && l.estadoContable !== estado) return false;
      if (!term) return true;
      return [
        l.boleta,
        l.finca,
        l.lote,
        l.bloque,
        l.estadio,
        l.labor,
        l.supervisor,
        l.cuenta,
        l.cuentaNombre,
      ].join(' ').toLowerCase().includes(term);
    });
    return sortRows(base, sort.value, (row, key) => {
      if (key === 'cuenta') return row.cuenta;
      if (key === 'horas') return row.horas + row.horasExtra;
      if (key === 'unidades') return row.unidades;
      if (key === 'costoLabor') return row.costoLabor;
      if (key === 'costoMaquinaria') return row.costoMaquinaria;
      if (key === 'costoTotal') return row.costoTotal;
      if (key === 'estadoContable') return row.estadoContable;
      return row[key];
    });
  }, [lineas, q, estado, sort.value]);

  const totales = useMemo(() => filtradas.reduce((acc, l) => ({
    costo: acc.costo + l.costoTotal,
    costoLabor: acc.costoLabor + l.costoLabor,
    costoMaquinaria: acc.costoMaquinaria + l.costoMaquinaria,
    horas: acc.horas + l.horas,
    horasExtra: acc.horasExtra + l.horasExtra,
    unidades: acc.unidades + l.unidades,
  }), { costo: 0, costoLabor: 0, costoMaquinaria: 0, horas: 0, horasExtra: 0, unidades: 0 }), [filtradas]);

  function exportarCsv() {
    const headers = [
      'Fecha',
      'Semana',
      'Boleta',
      'Finca',
      'Lote',
      'Bloque',
      'Estadio',
      'Labor',
      'Modalidad',
      'Supervisor',
      'Horas',
      'Horas extra',
      'Unidades',
      'Maquinaria',
      'Costo labor',
      'Costo maquinaria',
      'Costo total',
      'Cuenta',
      'Cuenta nombre',
      'Centro costo',
      'Estado contable',
    ];
    const lines = filtradas.map((l) => [
      l.fecha,
      l.semana,
      l.boleta,
      l.finca,
      l.lote,
      l.bloque,
      l.estadio,
      l.labor,
      l.modalidad,
      l.supervisor,
      l.horas.toFixed(2),
      l.horasExtra.toFixed(2),
      l.unidades.toFixed(2),
      l.maquinaria,
      l.costoLabor.toFixed(2),
      l.costoMaquinaria.toFixed(2),
      l.costoTotal.toFixed(2),
      l.cuenta,
      l.cuentaNombre,
      l.centroCosto,
      l.estadoContable,
    ].map(csvCell).join(','));
    const blob = new Blob([[headers.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labores-costeo-contable-${fechaDesde}-${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function prepararLoteCosteo() {
    const listas = filtradas.filter((l) => l.estadoContable === 'listo');
    if (listas.length === 0) {
      setError('No hay lineas listas para preparar. Apruebe las boletas y verifique que tengan costo.');
      return;
    }

    setPreparando(true);
    setError('');
    setMensaje('');

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id ?? null;
    const totalManoObra = listas.reduce((s, l) => s + l.costoLabor, 0);
    const totalMaquinaria = listas.reduce((s, l) => s + l.costoMaquinaria, 0);
    const totalCosto = listas.reduce((s, l) => s + l.costoTotal, 0);

    const { data: lote, error: loteError } = await supabase
      .from('campo_costeo_lotes')
      .insert({
        empresa_id: empresaId,
        periodo_desde: fechaDesde,
        periodo_hasta: fechaHasta,
        estado: 'preparado',
        origen: 'aplicaciones_labores',
        total_mano_obra: totalManoObra,
        total_maquinaria: totalMaquinaria,
        total_costo: totalCosto,
        preparado_por: userId,
        preparado_at: new Date().toISOString(),
        created_by: userId,
        updated_by: userId,
        observacion: `Preparado desde Costeo Contable de Labores (${listas.length} lineas).`,
      })
      .select('id')
      .single();

    if (loteError || !lote?.id) {
      setError(
        loteError?.code === '42P01'
          ? 'Falta aplicar la migracion de costeo contable antes de preparar lotes.'
          : loteError?.message || 'No se pudo crear el lote de costeo.'
      );
      setPreparando(false);
      return;
    }

    const detalle = listas.map((l) => ({
      empresa_id: empresaId,
      costeo_lote_id: lote.id,
      boleta_id: l.boletaId,
      boleta_numero: l.boleta,
      fecha: l.fecha,
      semana: l.semana,
      finca_id: l.fincaId,
      finca_nombre: l.finca === '-' ? null : l.finca,
      lote_id: l.loteId,
      lote_num: l.lote === '-' ? null : l.lote,
      bloque_id: l.bloqueId,
      bloque_num: l.bloque === '-' ? null : l.bloque,
      estadio_codigo: l.estadio === '-' ? null : l.estadio,
      labor_id: l.laborId,
      labor_nombre: l.labor === '-' ? null : l.labor,
      modalidad: l.modalidad,
      supervisor_nombre: l.supervisor === '-' ? null : l.supervisor,
      equipo_id: l.equipoId,
      equipo_codigo: l.equipoCodigo || null,
      equipo_nombre: l.equipoNombre || null,
      horas_ordinarias: l.horas,
      horas_extra: l.horasExtra,
      cantidad_produccion: l.unidades,
      costo_mano_obra: l.costoLabor,
      costo_maquinaria: l.costoMaquinaria,
      costo_total: l.costoTotal,
      cuenta_contable: l.cuenta,
      cuenta_nombre: l.cuentaNombre,
      centro_costo: l.centroCosto,
      estado_contable: l.estadoContable,
      metadata: {
        fuente: 'CosteoContableLaboresPage',
        generado_at: new Date().toISOString(),
      },
    }));

    const { error: detalleError } = await supabase
      .from('campo_costeo_lote_detalle')
      .insert(detalle);

    if (detalleError) {
      await supabase.from('campo_costeo_lotes').delete().eq('id', lote.id);
      setError(detalleError.message || 'No se pudo guardar el detalle del lote de costeo.');
      setPreparando(false);
      return;
    }

    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'preparar_lote_costeo',
      entidad: 'campo_costeo_lotes',
      entidadId: lote.id,
      descripcion: `Lote de costeo preparado (${listas.length} lineas, ${fmtCRC(totalCosto)})`,
    });
    setMensaje(`Lote de costeo preparado: ${lote.id.slice(0, 8)} (${listas.length} lineas, ${fmtCRC(totalCosto)}).`);
    setPreparando(false);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Costeo Contable de Labores</h1>
            <p className={pageSubtitleCls}>Base aprobada para ERP-MYA: mano de obra, maquinaria, lote, bloque, estadio y cuenta sugerida</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5 xl:flex xl:items-end">
            <div>
              <label className={labelCls}>Desde</label>
              <input type="date" className={inputCls} value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input type="date" className={inputCls} value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select className={selectCls} value={estado} onChange={(e) => setEstado(e.target.value as typeof estado)}>
                <option value="todos">Todos</option>
                <option value="listo">Listos</option>
                <option value="pendiente">Pendientes</option>
                <option value="bloqueado">Bloqueados</option>
              </select>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            {permisos.perm_administrar && (
              <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={prepararLoteCosteo} disabled={preparando || loading || filtradas.every((l) => l.estadoContable !== 'listo')}>
                {preparando ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} />}
                Preparar lote
              </button>
            )}
            <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={exportarCsv} disabled={filtradas.length === 0}>
              <Download size={14} />
              Exportar
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {mensaje && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-success-bg)', borderColor: 'var(--tone-success-border)', color: 'var(--tone-success-ink)' }}>
            <BookOpenCheck size={17} style={{ color: 'var(--tone-success-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{mensaje}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <Kpi label="Costo total" value={fmtCRC(totales.costo)} icon={<Calculator size={14} />} tone="var(--tone-success-text)" />
          <Kpi label="Mano de obra" value={fmtCRC(totales.costoLabor)} icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
          <Kpi label="Maquinaria" value={fmtCRC(totales.costoMaquinaria)} icon={<Calculator size={14} />} tone="var(--tone-warn-text)" />
          <Kpi label="Horas / Unid." value={`${fmtNum(totales.horas + totales.horasExtra)} h / ${fmtNum(totales.unidades)}`} icon={<BookOpenCheck size={14} />} tone="var(--tone-purple-text)" />
        </div>

        <div className="mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input className={`${inputCls} pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Boleta, lote, bloque, labor, cuenta o supervisor" />
              </div>
            </div>
            <div className="text-xs" style={{ color: 'var(--ink-faint)' }}>
              {filtradas.length} linea(s) / {boletas.length} boleta(s)
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando costeo...
          </div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-lg border px-4 py-12 text-center text-sm" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
            No hay labores con costo en el rango seleccionado.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="min-w-[1420px] w-full border-collapse">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}><SortableTh sort={sort} sortKey="fecha">Fecha</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="boleta">Boleta</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="finca">Ubicacion</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="labor">Labor</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="cuenta">Cuenta sugerida</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="horas" align="right">Horas</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="unidades" align="right">Unid.</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="costoLabor" align="right">Labor</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="costoMaquinaria" align="right">Maq.</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="costoTotal" align="right">Total</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="estadoContable">Estado</SortableTh></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((l) => (
                  <tr key={l.id} className={trCls}>
                    <td className={tdCls}>
                      <div className="font-mono" style={{ color: 'var(--ink)' }}>{fmtDate(l.fecha)}</div>
                      <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Sem. {l.semana}</div>
                    </td>
                    <td className={tdCls}>
                      <div className="font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{l.boleta}</div>
                      <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{l.supervisor}</div>
                    </td>
                    <td className={tdCls}>
                      <div style={{ color: 'var(--ink)' }}>{l.finca}</div>
                      <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Lote {l.lote} / Bloque {l.bloque} / Est. {l.estadio}</div>
                    </td>
                    <td className={tdCls}>
                      <div style={{ color: 'var(--ink)' }}>{l.labor}</div>
                      <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{l.modalidad} · {l.unidad} · {l.maquinaria}</div>
                    </td>
                    <td className={tdCls}>
                      <div className="font-mono font-bold" style={{ color: 'var(--tone-purple-text)' }}>{l.cuenta}</div>
                      <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{l.cuentaNombre}</div>
                      <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{l.centroCosto}</div>
                    </td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtNum(l.horas + l.horasExtra)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtNum(l.unidades)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(l.costoLabor)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(l.costoMaquinaria)}</td>
                    <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(l.costoTotal)}</td>
                    <td className={tdCls}><EstadoContableBadge estado={l.estadoContable} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 rounded-lg border px-4 py-3 text-xs leading-5" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
          Esta vista no calcula la nomina ni genera asientos definitivos. Prepara la base aprobada para Contabilidad:
          cuentas sugeridas, centros de costo y distribucion por lote/bloque/estadio.
        </div>
      </div>
    </div>
  );
}

export default CosteoContableLaboresPage;
